import type { PaymentProvider, StoreProductInfo } from "./paymentPorts.js";

export interface StoreProductDefinition {
  /** 게임 내부 논리 product id. */
  readonly productId: string;
  /** 네이티브 스토어 product id/SKU. */
  readonly storeProductId: string;
}

/** 앱 상품 정의와 네이티브 스토어 조회 결과를 매핑/캐시하는 결제 패키지 공용 카탈로그. */
export class StoreProductCatalog {
  private readonly storeIdByProductId = new Map<string, string>();
  private readonly storeProductIds: readonly string[];
  private productsByStoreId = new Map<string, StoreProductInfo>();

  constructor(products: readonly StoreProductDefinition[]) {
    this.storeProductIds = products.map((p) => p.storeProductId);
    products.forEach((p) => this.storeIdByProductId.set(p.productId, p.storeProductId));
  }

  async refresh(provider: PaymentProvider): Promise<boolean> {
    const available = await provider.getAvailableProducts(this.storeProductIds);
    const next = new Map(available.map((p) => [p.storeProductId, p]));
    if (storeProductMapsEqual(this.productsByStoreId, next)) return false;
    this.productsByStoreId = next;
    return true;
  }

  isAvailable(productId: string): boolean {
    return this.productsByStoreId.has(this.storeProductIdOf(productId));
  }

  priceLabel(productId: string): string | undefined {
    return this.productsByStoreId.get(this.storeProductIdOf(productId))?.priceLabel;
  }

  storeProductIdOf(productId: string): string {
    return this.storeIdByProductId.get(productId) ?? productId;
  }
}

function storeProductMapsEqual(
  a: ReadonlyMap<string, StoreProductInfo>,
  b: ReadonlyMap<string, StoreProductInfo>,
): boolean {
  if (a.size !== b.size) return false;
  for (const [key, value] of a) {
    if (b.get(key)?.priceLabel !== value.priceLabel) return false;
  }
  return true;
}
